import React from 'react';
import { AlertTriangle, RefreshCw, Home } from 'lucide-react';
import * as Sentry from "@sentry/react";

class ErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { hasError: false, error: null, errorInfo: null };
    }

    static getDerivedStateFromError(error) {
        return { hasError: true };
    }

    componentDidCatch(error, errorInfo) {
        // Check for chunk load errors (Deployment version mismatch)
        const isChunkError = error.message?.includes('Failed to fetch dynamically imported module') ||
            error.message?.includes('Loading chunk') ||
            error.message?.includes('Importing a module script failed');

        if (isChunkError) {
            const lastReload = sessionStorage.getItem('chunk_error_reload');
            const now = Date.now();

            // Prevent infinite reload loops (limit to once per 10s)
            if (!lastReload || (now - parseInt(lastReload)) > 10000) {
                console.log('Chunk load error detected. Reloading...');
                sessionStorage.setItem('chunk_error_reload', now.toString());
                window.location.reload();
                return;
            }
        }

        // Log error to console in development
        console.error('Error caught by boundary:', error, errorInfo);

        this.setState({
            error,
            errorInfo
        });

        // Send to Sentry in production
        if (import.meta.env.VITE_SENTRY_DSN) {
            Sentry.captureException(error, {
                contexts: {
                    react: {
                        componentStack: errorInfo.componentStack,
                    },
                },
            });
        }
    }

    handleReset = () => {
        this.setState({ hasError: false, error: null, errorInfo: null });
    };

    handleGoHome = () => {
        window.location.href = '/';
    };

    render() {
        if (this.state.hasError) {
            const { error, errorInfo } = this.state;
            const isDevelopment = import.meta.env.DEV;

            return (
                <div className="min-h-screen bg-base text-main font-mono flex items-center justify-center p-4">
                    <div className="max-w-2xl w-full">
                        {/* Error Card */}
                        <div className="bg-gradient-to-br from-red-500/10 to-orange-500/10 border-2 border-red-500/30 rounded-2xl p-8 shadow-2xl">
                            {/* Icon */}
                            <div className="flex justify-center mb-6">
                                <div className="w-20 h-20 bg-red-500/20 rounded-full flex items-center justify-center">
                                    <AlertTriangle size={48} className="text-red-500" />
                                </div>
                            </div>

                            {/* Title */}
                            <h1 className="text-3xl font-bold text-center text-red-500 mb-4">
                                Oops! Something went wrong
                            </h1>

                            {/* User-friendly message */}
                            <p className="text-center text-main mb-6 leading-relaxed">
                                Don't worry - this happens sometimes. Your progress is saved,
                                and you can try refreshing the page or returning to the main menu.
                            </p>

                            {/* Action Buttons */}
                            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-6">
                                <button
                                    onClick={this.handleReset}
                                    className="flex items-center justify-center gap-2 px-6 py-3 bg-primary text-primary-content rounded-xl font-bold hover:bg-primary/90 transition-colors shadow-lg hover:shadow-primary/50">
                                    <RefreshCw size={20} />
                                    Try Again
                                </button>
                                <button
                                    onClick={this.handleGoHome}
                                    className="flex items-center justify-center gap-2 px-6 py-3 bg-surface text-main border-2 border-border hover:border-primary rounded-xl font-bold transition-colors">
                                    <Home size={20} />
                                    Go to Main Menu
                                </button>
                            </div>

                            {/* Developer Info (only in development) */}
                            {isDevelopment && (
                                <details className="mt-6 bg-base/50 rounded-xl p-4 border border-border">
                                    <summary className="cursor-pointer text-sm font-bold text-muted hover:text-main mb-2">
                                        🐛 Developer Info (Dev Mode Only)
                                    </summary>
                                    <div className="mt-4 space-y-3">
                                        {error && (
                                            <div>
                                                <p className="text-xs font-bold text-red-400 mb-1">Error:</p>
                                                <pre className="text-xs bg-black/30 p-3 rounded overflow-x-auto text-red-300">
                                                    {error.toString()}
                                                </pre>
                                            </div>
                                        )}
                                        {errorInfo && (
                                            <div>
                                                <p className="text-xs font-bold text-orange-400 mb-1">Component Stack:</p>
                                                <pre className="text-xs bg-black/30 p-3 rounded overflow-x-auto text-orange-300">
                                                    {errorInfo.componentStack}
                                                </pre>
                                            </div>
                                        )}
                                    </div>
                                </details>
                            )}

                            {/* Help Text */}
                            <p className="text-xs text-muted text-center mt-6">
                                💡 If this keeps happening, try clearing your browser cache or updating your browser.
                            </p>
                        </div>
                    </div>
                </div>
            );
        }

        return this.props.children;
    }
}

export default ErrorBoundary;
